import { fak, IconDefinition } from '@awesome.me/kit-da59d5031a/icons';

import sortBy from 'lodash/sortBy';
import { CareType, isSkilledNursingCareType } from 'src/constants/CareTypes';
import { ObservationType } from 'src/store/sageCareServiceApi';

export interface ObservationDescriptor {
    value: string;
    label: string;
}

export interface ObservationOption {
    icon?: IconDefinition;
    id: ObservationType;
    displayName: string;
    sortOrder?: number;
    descriptors?: ObservationDescriptor[];
}

/** Display name overrides for SNF care types. Falls back to `displayName` otherwise. */
const SkilledNursingDisplayNameOverrides: Partial<Record<ObservationType, string>> = {
    PAIN: 'Complains of pain/discomfort',
};

const baseObservationTypes: Record<string, ObservationOption> = {
    // Defaults
    Pain: {
        icon: fak.faComplainsOfPainDiscomfort,
        id: 'PAIN',
        displayName: 'Pain',
        sortOrder: 0,
        descriptors: [
            { value: 'REPORTS_PAIN', label: 'Reports pain' },
            { value: 'REQUESTS_PAIN_MEDICATION', label: 'Requests pain medication' },
            { value: 'COUGHING', label: 'Coughing' },
        ],
    },
    AbnormalBowelMovement: {
        icon: fak.faAbnormalBowelMovement,
        id: 'BOWEL_MOVEMENT',
        displayName: 'Bowel movement',
        sortOrder: 1,
        descriptors: [
            { value: 'MORE_FREQUENT', label: 'More frequent' },
            { value: 'LESS_FREQUENT', label: 'Less frequent' },
            { value: 'LOOSE_WATERY_STOOL', label: 'Loose/watery stool' },
        ],
    },
    AbnormalUrine: {
        icon: fak.faAbnormalUrine,
        id: 'URINE',
        displayName: 'Urine',
        sortOrder: 2,
        descriptors: [
            { value: 'MORE_FREQUENT', label: 'More frequent' },
            { value: 'LESS_FREQUENT', label: 'Less frequent' },
            { value: 'STRONG_SMELL', label: 'Strong smell' },
            { value: 'REPORTS_DISCOMFORT', label: 'Reports discomfort' },
        ],
    },
    SkinIssue: {
        icon: fak.faSkinIssue,
        id: 'SKIN',
        displayName: 'Skin',
        sortOrder: 3,
        descriptors: [
            { value: 'DISCOLORATION', label: 'Discoloration' },
            { value: 'OPEN_AREA', label: 'Open area' },
            { value: 'REPORTS_ITCHING', label: 'Reports itching' },
        ],
    },
    MoodBehaviorIssue: {
        icon: fak.faObsMoodBehavior,
        id: 'MOOD_BEHAVIOR',
        displayName: 'Mood/Behavior',
        sortOrder: 4,
        descriptors: [
            { value: 'TALKING_LESS_THAN_USUAL', label: 'Talking less than usual' },
            {
                value: 'NOT_PARTICIPATING_IN_USUAL_ACTIVITIES',
                label: 'Not participating in usual activities',
            },
            { value: 'SEEMS_DIFFERENT', label: 'Seems different' },
            { value: 'AGITATED_OR_COMBATIVE', label: 'Agitated or combative' },
            { value: 'REFUSING_CARE', label: 'Refusing care' },
        ],
    },
    EatingDrinkingLess: {
        icon: fak.faObsEatingDrinkingLess,
        id: 'EATING_DRINKING_LESS',
        displayName: 'Eating or drinking less',
        sortOrder: 5,
        descriptors: [
            { value: 'EATING_LESS', label: 'Eating less' },
            { value: 'DRINKING_LESS', label: 'Drinking less' },
            { value: 'REFUSING_MEAL_DRINK', label: 'Refusing meal or drink' },
            { value: 'NEEDING_MORE_HELP', label: 'Needing more help to eat' },
        ],
    },

    // Optional / deprecated — sort after the defaults
    NeedsMoreHelp: {
        icon: fak.faNeedsMoreHelp,
        id: 'NEEDS_MORE_HELP',
        displayName: 'Needs more help',
        sortOrder: 6,
    },
    SeemsDifferent: {
        icon: fak.faSeemsDifferent,
        id: 'SEEMS_DIFFERENT',
        displayName: 'Seems different',
        sortOrder: 7,
    },
    Other: {
        icon: fak.faOtherRequest,
        id: 'OTHER',
        displayName: 'Other',
        sortOrder: 8,
    },
};

export const getObservationOptionArrayFromIds = (
    observationTypes: ObservationType[]
): Array<ObservationOption> => {
    const baseTypeMap = Object.fromEntries(
        Object.values(baseObservationTypes).map((opt) => [opt.id, opt])
    );

    return sortBy(
        observationTypes.map((type) =>
            baseTypeMap[type] ? { ...baseTypeMap[type] } : { id: type, displayName: type }
        ),
        'sortOrder'
    );
};

export const IdToObservationTypeMap = new Map(
    Object.values(baseObservationTypes).map((type) => [type.id, type])
);

/**
 * Resolve an observation type's display name, applying SNF overrides
 * (LongTermCare, ShortTermRehab, SkilledNursing) when present.
 */
export const getObservationDisplayName = (
    observationType: ObservationType,
    careType?: CareType | string
): string => {
    if (careType && isSkilledNursingCareType(careType)) {
        const override = SkilledNursingDisplayNameOverrides[observationType];
        if (override) return override;
    }
    return IdToObservationTypeMap.get(observationType)?.displayName ?? observationType;
};

/**
 * Map a list of descriptor values (e.g. ['MORE_FREQUENT', 'REPORTS_DISCOMFORT']) for an
 * observation type to their human-readable labels (e.g. 'More frequent, Reports discomfort'),
 * joined with ", ". Unknown values pass through unchanged.
 */
export const getDescriptorLabels = (
    observationType: ObservationType,
    descriptorValues: string[]
): string => {
    const observationOption = IdToObservationTypeMap.get(observationType);
    return descriptorValues
        .map(
            (value) =>
                observationOption?.descriptors?.find((d) => d.value === value)?.label ?? value
        )
        .join(', ');
};
